import type { Selection } from "../../charts/utils";
import type { LevelConfig } from "../../modules/emissions/data-config";

interface NavBreadcrumbsProps {
    level: number;
    selection: Selection;
    levelConfigs: {
        level1: LevelConfig;
        level2: LevelConfig;
        level3: LevelConfig;
    };
    onNavigate: (targetLevel: number) => void;
}

const findLabel = (config: LevelConfig, key: string | null) => {
    if (!key) {
        return null;
    }
    return config.navItems.find((nav) => nav.key === key)?.label || key;
};

export const NavBreadcrumbs: React.FC<NavBreadcrumbsProps> = ({ level, selection, levelConfigs, onNavigate }) => {
    const breadcrumbs: { label: string; targetLevel: number }[] = [{ label: "Totalt", targetLevel: 1 }];

    const level1Label = findLabel(levelConfigs.level1, selection.level1);
    if (level >= 2 && level1Label) {
        breadcrumbs.push({ label: level1Label, targetLevel: 2 });
    }

    const level2Label = findLabel(levelConfigs.level2, selection.level2);
    if (level >= 3 && level2Label) {
        breadcrumbs.push({ label: level2Label, targetLevel: 3 });
    }

    const level3Label = findLabel(levelConfigs.level3, selection.level3);
    if (level >= 4 && level3Label) {
        breadcrumbs.push({ label: level3Label, targetLevel: 4 });
    }

    const lastBreadcrumbIndex = breadcrumbs.length - 1;
    const isVisible = level >= 2;

    return (
        <nav
            aria-label="Navigasjonssti"
            aria-hidden={!isVisible}
            style={{
                // margin: '1rem 0',
                fontSize: "var(--ds-font-size-2)",
                color: "var(--ds-color-neutral-text-subtle)",
                minHeight: "2.5rem",
                visibility: isVisible ? "visible" : "hidden",
                pointerEvents: isVisible ? "auto" : "none",
            }}
        >
            <ol
                style={{
                    listStyle: "none",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "0.25rem",
                    padding: 0,
                    margin: 0,
                }}
            >
                {breadcrumbs.map((crumb, index) => {
                    const isCurrent = index === lastBreadcrumbIndex;
                    const key = `${crumb.label}-${index}`;
                    return (
                        <li key={key} style={{ display: "flex", alignItems: "center" }}>
                            {isCurrent ? (
                                <strong style={{ color: "var(--ds-color-neutral-text-default)" }}>{crumb.label}</strong>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => onNavigate(crumb.targetLevel)}
                                    style={{
                                        background: "none",
                                        border: "none",
                                        padding: 0,
                                        cursor: "pointer",
                                        color: "var(--ds-color-action-text-primary)",
                                        font: "inherit",
                                    }}
                                >
                                    {crumb.label}
                                </button>
                            )}
                            {index < lastBreadcrumbIndex && (
                                <span aria-hidden="true" style={{ margin: "0 0.25rem" }}>
                                    &gt;
                                </span>
                            )}
                        </li>
                    );
                })}
            </ol>
        </nav>
    );
};
